import React, { useEffect, useState } from "react";
import BatchRecordingListing from "./BatchRecordingListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setTotalItems,
  setItems,
} from "src/redux/slices/BatchRecordingSlice";
import AddBatchRecordingFormWrapper from "../Add/AddBatchRecordingFormWrapper";
import {
  useGetAllRecordingQuery,
  useDeleteRecordingMutation,
} from "src/services/BatchServices";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { RxPencil1 } from "react-icons/rx";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { MdDeleteOutline } from "react-icons/md";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import EditBatchRecordingFormWrapper from "../Edit/EditBatchRecordingFormWrapper";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { useParams } from "react-router-dom";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const BatchRecordingListingWrapper = (props: Props) => {
  const dispatch = useDispatch();

  const { batchId } = useParams();

  const [deleteRecording] = useDeleteRecordingMutation();

  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.batchRecording);

  const { data, isLoading, isFetching } = useGetAllRecordingQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["description" , 'date'],
    page: page,
    filterBy: [{
      fieldName: 'batchId' ,
      value: [batchId] 
    }],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (recordingId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedId(recordingId);
          setIsOpenEditForm(true);
        },
        moduleName: UserModuleNameTypes.BATCHES,
        actionName: UserModuleActionTypes.EDIT,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Recording ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteRecording(recordingId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        moduleName: UserModuleNameTypes.BATCHES,
        actionName: UserModuleActionTypes.DELETE,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "date",
      headerName: "Date",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div className="text-slate-700 font-medium">
          {formatedDateTimeIntoIst(row?.date, "DD MMM yyyy")}
        </div>
      ),
    },
    {
      noAuthRequired: true,
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <div>
      <BatchRecordingListing
       columns={isCheckColumnPermission({
        moduleName: UserModuleNameTypes.BATCHES,
        actionName: UserModuleActionTypes.BATCHES_RECORDINGS_AND_NOTES_LIST,
        columns: columns,
      })}
        rows={items}
        onAddNew={() => {
          setIsOpenAddForm(true);
        }}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
      {isOpenAddForm && (
        <AddBatchRecordingFormWrapper
          onClose={() => {
            setIsOpenAddForm(false);
          }}
        />
      )}

      {isOpenEditForm && (
        <EditBatchRecordingFormWrapper
          recordingId={selectedId}
          onClose={() => {
            setIsOpenEditForm(false);
          }}
        />
      )}
    </div>
  );
};

export default BatchRecordingListingWrapper;
